import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  api,
  can,
  getToken,
  narrowestRoleWith,
  refusalReason,
  rolesWith,
  setToken,
  shortDate,
  type Assessment,
  type Campaign,
  type CampaignRecipient,
  type Me,
} from '../lib/api'
import { mailWent, notEmailed, whyNotEmailed } from '../lib/mail'
import { CampaignNotEmailed } from '../components/CampaignNotEmailed'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { StageTrack, Status, Toggletip, type Stage, type StageState } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { Crumbs } from '../components/Crumbs'
import '../components/kit/back.css'
import {
  CAMPAIGN_STAGE_NAMES,
  CampaignStatus,
  DeliveryState,
  campaignStages,
  mailOutcome,
  modeLabel,
  summariseRecipients,
  type MailSummary,
} from './Campaigns'
import '../styles/sweep.css'

// Approving is the moment of no return on this product: it is the only act
// that issues an assessment and puts mail in the outbox. So the screen states
// what the press will do before it is pressed, and states what it did
// afterwards by name rather than as a count.
//
// Where the campaign has got to is drawn as its stage track, from the status
// alone, and each panel keeps one line with its reason behind an (i) or in a
// drawer, so the act and its consequence are what the eye lands on.

// A sentence the API wrote is shown as written. It stays on the line when it
// fits one; a longer one opens in full from the (i) beside a short lead.
function SaidLine({ lead, label, text }: { lead: string; label: string; text: string }) {
  if (text.length <= 80) return <p style={{ margin: 0 }}>{text}</p>
  return (
    <p className="sw-row" style={{ margin: 0 }}>
      <span>{lead}</span>
      <ReasonTip label={label} text={text} />
    </p>
  )
}

// The three stages at the top of the Approval panel, with a sub-line only
// where the record carries the fact: who raised it, who approved it and when,
// that it was cancelled, that its invitations were not emailed, and nothing
// guessed for the rest.
export function trackStages(c: Campaign, states: StageState[], mail?: MailSummary | null): Stage[] {
  const drafted = states[0] ?? 'upcoming'
  const approved = states[1] ?? 'upcoming'
  const sent = states[2] ?? 'upcoming'
  const outcome = mailOutcome(mail)
  const third: Stage =
    outcome === 'all'
      ? { key: 'sent', label: 'Not emailed', state: sent, sub: 'Recorded, not emailed' }
      : outcome === 'some'
        ? { key: 'sent', label: CAMPAIGN_STAGE_NAMES[2], state: sent, sub: mail?.notEmailed + ' of ' + mail?.issued + ' not emailed' }
        : { key: 'sent', label: CAMPAIGN_STAGE_NAMES[2], state: sent }
  return [
    { key: 'drafted', label: CAMPAIGN_STAGE_NAMES[0], state: drafted, sub: 'By ' + c.createdBy },
    {
      key: 'approved',
      label: CAMPAIGN_STAGE_NAMES[1],
      state: approved,
      sub: c.approvedBy
        ? c.approvedBy + (c.approvedAt ? ', ' + shortDate(c.approvedAt) : '')
        : approved === 'current'
          ? 'Needs a second person'
          : approved === 'rejected'
            ? 'Cancelled'
            : undefined,
    },
    third,
  ]
}

// The statuses the database treats as an open assessment. The list agrees with
// ux_one_open_assessment; if it drifts, the prediction on this screen starts
// promising invitations the API will suppress.
const OPEN_STATUSES = new Set(['DRAFT', 'ISSUED', 'IN_PROGRESS', 'SUBMITTED', 'UNDER_REVIEW', 'RETURNED'])

// The detail endpoint answers with the campaign row, which carries
// recipientCount and the bound versions rather than the issued and suppressed
// tallies the list endpoint computes. Reading c.recipients here would print
// undefined, so the counts on this screen are derived from the recipients.
type CampaignRow = Campaign & {
  recipientCount?: number
  questionnaireVersionId?: string
  riskModelVersionId?: string
}

type RecipientRow = CampaignRecipient & { vendorCode?: string }

type IssuedRow = {
  vendorId?: string
  vendorCode?: string
  vendorName?: string
  email?: string
  assessmentId?: string
  reference?: string
}

type SuppressedRow = {
  vendorId?: string
  vendorCode?: string
  vendorName?: string
  deferredToReference?: string
  reason?: string
}

type FailedRow = { vendorId?: string; vendorCode?: string; reason?: string }

type Outcome = {
  issued: IssuedRow[]
  suppressed: SuppressedRow[]
  failed: FailedRow[]
  issuedCount: number
  suppressedCount: number
  failedCount: number
  entitlementStamp?: string
  note?: string
  // Whether the invitations it queued will be emailed. See lib/mail.ts.
  mail?: string
}

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : []
}

// The approve endpoint answers with named lists and a counts object, while the
// typed client describes tallies. Both shapes are read here so that a change on
// either side degrades to a smaller answer rather than to a blank panel.
function readOutcome(raw: unknown): Outcome {
  const o = (raw ?? {}) as Record<string, unknown>
  const counts = (o.counts ?? {}) as Record<string, unknown>
  const issued = arr<IssuedRow>(o.issued)
  const suppressed = arr<SuppressedRow>(o.deferred).concat(arr<SuppressedRow>(o.suppressedVendors))
  const failed = arr<FailedRow>(o.failed)

  const count = (fromCounts: unknown, fromField: unknown, list: unknown[]): number => {
    if (typeof fromCounts === 'number') return fromCounts
    if (typeof fromField === 'number') return fromField
    return list.length
  }

  return {
    issued,
    suppressed,
    failed,
    issuedCount: count(counts.issued, o.issued, issued),
    // Deferred to an open assessment, plus left out by the due diligence gate
    // at approval (counts.suppressed, release 3.3).
    suppressedCount: count(
      typeof counts.deferred === 'number'
        ? counts.deferred + (typeof counts.suppressed === 'number' ? counts.suppressed : 0)
        : undefined,
      o.suppressed,
      suppressed,
    ),
    failedCount: count(counts.failed, o.failed, failed),
    entitlementStamp: typeof o.entitlementStamp === 'string' ? o.entitlementStamp : undefined,
    note: typeof o.note === 'string' ? o.note : undefined,
    mail: typeof o.mail === 'string' ? o.mail : undefined,
  }
}

function vendorLabel(r: { vendorName?: string; vendorCode?: string; vendorId?: string }): string {
  return r.vendorName || r.vendorCode || r.vendorId || 'a vendor'
}

// Dev auth reads the subject off the end of the token, so identity is a thing
// the reader can actually change to try the refusal. The roles are carried
// across rather than replaced: reading the console needs a read role and
// approving needs an issuing role, and a token that says only tenant_approver
// loads a console that cannot read its own campaign list. Which roles those
// are is the catalogue's answer, so this suggestion cannot go stale the way
// a list written out here would.
function suggestToken(current: string, tenantId: string, subject: string): string {
  const parts = current.startsWith('dev:') ? current.slice(4).split(':') : []
  const tenant = parts[0] || tenantId
  const roles = (parts[1] || 'tprm_owner')
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean)
  const issuers = rolesWith('questionnaire.issue')
  const narrowest = narrowestRoleWith('questionnaire.issue')
  if (narrowest && !roles.some((r) => issuers.includes(r))) roles.push(narrowest)
  return 'dev:' + tenant + ':' + roles.join(',') + ':' + subject
}

export function CampaignDetail({ id }: { id: string }) {
  const [campaign, setCampaign] = useState<CampaignRow | null>(null)
  const [recipients, setRecipients] = useState<RecipientRow[]>([])
  // What the worker will do now with an invitation it has not reached.
  const [mailNow, setMailNow] = useState<string | undefined>(undefined)
  const [error, setError] = useState<unknown>(null)
  const [me, setMe] = useState<Me | null>(null)
  const [openByVendor, setOpenByVendor] = useState<Map<string, string> | null>(null)

  const [armed, setArmed] = useState(false)
  const [approving, setApproving] = useState(false)
  const [approveError, setApproveError] = useState<unknown>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelIssue, setCancelIssue] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<unknown>(null)
  const [cancelling, setCancelling] = useState(false)

  const load = useCallback(() => {
    return api
      .campaign(id)
      .then((d) => {
        setCampaign(d.campaign as CampaignRow)
        setRecipients((d.recipients ?? []) as RecipientRow[])
        setMailNow(d.mail)
        setError(null)
      })
      .catch((err) => setError(err))
  }, [id])

  useEffect(() => {
    let live = true
    setCampaign(null)
    setRecipients([])
    setError(null)
    setOutcome(null)
    setArmed(false)
    setApproveError(null)

    api
      .campaign(id)
      .then((d) => {
        if (!live) return
        setCampaign(d.campaign as CampaignRow)
        setRecipients((d.recipients ?? []) as RecipientRow[])
        setMailNow(d.mail)
      })
      .catch((err) => {
        if (live) setError(err)
      })

    api.me().then(
      (m) => {
        if (live) setMe(m)
      },
      () => {
        // The header bar already reports a broken token. This screen only
        // needs the subject to warn about self-approval, and it warns less
        // rather than blocking the page.
      },
    )

    // One call answers the collision question for every recipient. Asking per
    // vendor would be three hundred requests to render one paragraph.
    api.assessments().then(
      (a) => {
        if (!live) return
        const m = new Map<string, string>()
        for (const x of a.assessments as Assessment[]) {
          if (OPEN_STATUSES.has(x.status) && !m.has(x.vendorId)) m.set(x.vendorId, x.reference)
        }
        setOpenByVendor(m)
      },
      () => {
        if (live) setOpenByVendor(null)
      },
    )

    return () => {
      live = false
    }
  }, [id])

  const counts = useMemo(() => {
    const issued = recipients.filter((r) => r.assessmentId).length
    const suppressed = recipients.filter((r) => r.deliveryState === 'SUPPRESSED').length
    const pending = recipients.filter((r) => !r.assessmentId && r.deliveryState === 'PENDING').length
    const notMailed = recipients.filter((r) => notEmailed(r.mail)).length
    return { total: recipients.length, issued, suppressed, pending, notMailed }
  }, [recipients])

  // Whether the invitations were emailed, from what the worker recorded and
  // what it will do with the ones it has not reached (Campaigns.tsx).
  const mail = useMemo(() => summariseRecipients(recipients, mailNow), [recipients, mailNow])
  // Before approval: whether approving will email anybody.
  const willEmail = !notEmailed(mailNow)

  // What approval will do, from the recipients that are still pending and the
  // assessments open right now. It is a forecast and is labelled as one: a
  // vendor that submits in the next minute moves between the two numbers.
  const forecast = useMemo(() => {
    const willIssue: RecipientRow[] = []
    const willSuppress: { row: RecipientRow; reference: string }[] = []
    for (const r of recipients) {
      if (r.assessmentId || r.deliveryState !== 'PENDING') continue
      const open = openByVendor?.get(r.vendorId)
      if (open) willSuppress.push({ row: r, reference: open })
      else willIssue.push(r)
    }
    return { willIssue, willSuppress }
  }, [recipients, openByVendor])

  if (error != null) return <ErrorBox error={error} what="This campaign" />
  if (!campaign) return <Loading what="the campaign" />

  const holding = campaign.status === 'DRAFT' || campaign.status === 'PENDING_APPROVAL'
  const isMaker = me != null && me.subject === campaign.createdBy
  const approveErr = approveError instanceof ApiError ? approveError : null
  const selfApproval = approveErr?.status === 409 && approveErr.code === 'SELF_APPROVAL'
  const notApprovable = approveErr?.status === 409 && approveErr.code !== 'SELF_APPROVAL'

  const suggestedSubject = campaign.createdBy === 'checker@demo.example' ? 'approver.two@demo.example' : 'checker@demo.example'
  const suggestedToken = suggestToken(getToken(), me?.tenant.id ?? '', suggestedSubject)
  const canIssue = can('questionnaire.issue')
  const canPlan = can('assessment.plan')
  const track = campaignStages(campaign.status, mail)

  function approve() {
    setApproving(true)
    setApproveError(null)
    api
      .approveCampaign(id)
      .then((res) => {
        setOutcome(readOutcome(res))
        setArmed(false)
        return load()
      })
      .catch((err) => {
        setApproveError(err)
        setArmed(false)
      })
      .finally(() => setApproving(false))
  }

  function cancel(e: React.FormEvent) {
    e.preventDefault()
    setCancelError(null)
    if (cancelReason.trim().length < 4) {
      setCancelIssue('Say why this campaign is being stopped, the cancellation is recorded against you')
      return
    }
    setCancelIssue(null)
    setCancelling(true)
    api
      .cancelCampaign(id, cancelReason.trim())
      .then(() => {
        setCancelOpen(false)
        setCancelReason('')
        return load()
      })
      .catch((err) => setCancelError(err))
      .finally(() => setCancelling(false))
  }

  const columns: Column<RecipientRow>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (r) => r.vendorName ?? r.vendorId,
      render: (r) => (
        <>
          <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(r.vendorId)}>
            {r.vendorName || r.vendorCode || r.vendorId}
          </a>
          {r.vendorCode && <span className="cell-sub mono">{r.vendorCode}</span>}
        </>
      ),
    },
    {
      key: 'email',
      header: 'Invited address',
      sortValue: (r) => r.email,
      render: (r) => (r.email ? <span className="mono">{r.email}</span> : <span className="muted">no address on file</span>),
    },
    {
      key: 'state',
      header: 'Delivery',
      sortValue: (r) => r.deliveryState,
      render: (r) => <DeliveryState state={r.deliveryState} mail={r.mail} />,
    },
    {
      key: 'assessment',
      header: 'Assessment',
      sortValue: (r) => r.assessmentReference ?? null,
      render: (r) =>
        r.assessmentId && r.assessmentReference ? (
          <a className="cell-primary mono" href={'#/assessment/' + encodeURIComponent(r.assessmentId)}>
            {r.assessmentReference}
          </a>
        ) : (
          <span className="muted">none issued</span>
        ),
    },
    {
      key: 'reason',
      header: 'Reason',
      sortValue: (r) => (notEmailed(r.mail) ? r.mailReason ?? null : r.failureReason ?? null),
      // An invitation recorded and not emailed shows why in words, not the
      // stored reason with its MUTED: or OFF: in front.
      render: (r) =>
        notEmailed(r.mail) ? (
          <span>{r.mailReason || whyNotEmailed(r.mail ?? undefined)}</span>
        ) : r.failureReason ? (
          <span>{r.failureReason}</span>
        ) : (
          <span className="muted">-</span>
        ),
    },
  ]

  return (
    <>
      <div className="page-head">
        <div>
          <Crumbs className="kit-crumbs" home={{ label: 'Bulk assessments', href: '#/campaigns' }} current={campaign.name} />
          <h1>{campaign.name}</h1>
          <p className="muted" style={{ margin: 0 }}>
            {modeLabel(campaign.mode)}
            {/* One baseline of a bulk send (release 3.4, M7): the send is
                approved in one act from its own page. */}
            {campaign.batchId && (
              <>
                {' / '}
                <a href={'#/campaigns/new-batch?id=' + encodeURIComponent(campaign.batchId)}>Part of a bulk send</a>
              </>
            )}
          </p>
        </div>
        <CampaignStatus status={campaign.status} showMeaning mail={mail} />
      </div>

      <div className="grid-2">
        <div>
          <section className="panel" aria-labelledby="approval-heading">
            <h2 id="approval-heading">Approval</h2>

            {track && (
              <div style={{ margin: '4px 0 14px' }}>
                <StageTrack
                  stages={trackStages(campaign, track.states, mail)}
                  label={'Campaign stages, ' + track.label.toLowerCase()}
                />
              </div>
            )}

            {outcome && (
              <div className={'note' + (mailWent(outcome) ? '' : ' warn')} role="status">
                <strong>
                  Approved, {outcome.issuedCount} assessment{outcome.issuedCount === 1 ? '' : 's'} issued,{' '}
                  {outcome.suppressedCount} suppressed
                  {outcome.failedCount > 0 ? ', ' + outcome.failedCount + ' failed' : ''}
                </strong>
                {outcome.note ? (
                  <SaidLine lead="What the approval reported" label="What the approval reported" text={outcome.note} />
                ) : (
                  <p className="sw-row" style={{ margin: 0 }}>
                    <span>The invitations are queued and go out within a minute</span>
                    <Toggletip label="Delivery and retries">Delivery and retries are handled for you.</Toggletip>
                  </p>
                )}

                {outcome.issued.length > 0 && (
                  <>
                    <p className="outcome-head">
                      {mailWent(outcome)
                        ? 'Issued an assessment and queued an invitation'
                        : 'Issued an assessment; the invitation was not emailed'}
                    </p>
                    <ul className="outcome-list">
                      {outcome.issued.map((x, i) => (
                        <li key={(x.assessmentId ?? '') + i}>
                          <strong>{vendorLabel(x)}</strong>
                          {x.reference && (
                            <>
                              {' '}
                              <a className="mono" href={'#/assessment/' + encodeURIComponent(x.assessmentId ?? '')}>
                                {x.reference}
                              </a>
                            </>
                          )}
                          {x.email && <span className="cell-sub mono">{x.email}</span>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {outcome.suppressed.length > 0 && (
                  <>
                    <p className="outcome-head">
                      Suppressed on purpose, and not a failure of this campaign
                    </p>
                    <ul className="outcome-list">
                      {outcome.suppressed.map((x, i) => (
                        <li key={(x.vendorId ?? '') + i}>
                          <strong>{vendorLabel(x)}</strong>
                          <span className="cell-sub">
                            {x.reason ?? 'an assessment for this vendor is already open'}
                            {x.deferredToReference && !x.reason?.includes(x.deferredToReference)
                              ? ', reference ' + x.deferredToReference
                              : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {outcome.failed.length > 0 && (
                  <>
                    <p className="outcome-head">Failed, and needs a person</p>
                    <ul className="outcome-list">
                      {outcome.failed.map((x, i) => (
                        <li key={(x.vendorId ?? '') + i}>
                          <strong>{x.vendorCode || x.vendorId}</strong>
                          <span className="cell-sub">{x.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {outcome.entitlementStamp && (
                  <p className="field-note sw-row" style={{ margin: 0, maxWidth: 'none' }}>
                    <span>
                      Entitlement stamp <span className="mono">{outcome.entitlementStamp}</span>
                    </span>
                    <Toggletip label="What the entitlement stamp does">
                      Stamped on every assessment issued here. The vendor edge reads it and never calls the control plane,
                      so an outage there does not cost a vendor their work.
                    </Toggletip>
                  </p>
                )}
              </div>
            )}

            {!outcome && !holding && <CampaignNotEmailed mail={mail} />}

            {selfApproval && (
              <div className="note stop" role="alert">
                <strong>Refused: the person who raised a campaign cannot approve it</strong>
                <p>{approveErr?.message}</p>
                <p className="sw-row">
                  <span>Nothing was issued and nothing was queued</span>
                  <Toggletip label="Why the same person cannot approve">
                    This is maker-checker, and it is the control that stops mail to three hundred vendors being one
                    person's decision.
                  </Toggletip>
                </p>
                <dl className="kv" style={{ marginBottom: 10 }}>
                  <dt>Raised by</dt>
                  <dd className="mono">{campaign.createdBy}</dd>
                  <dt>You are</dt>
                  <dd className="mono">{me?.subject ?? 'unknown'}</dd>
                </dl>
                <p className="sw-row" style={{ marginBottom: 6 }}>
                  <span>For a local build with no sign-in</span>
                  <Toggletip label="Where acting as someone else works">
                    This local build has no sign-in, so it lets you act as a second person to see the approval through.
                    On UAT and production the approver signs in as themselves.
                  </Toggletip>
                </p>
                <button
                  type="button"
                  onClick={() => {
                    // Identity changes the tenant and the subject every view
                    // reads from, so everything on screen is stale the moment
                    // it changes.
                    setToken(suggestedToken)
                    window.location.reload()
                  }}
                >
                  Act as a second person and reload
                </button>{' '}
              </div>
            )}

            {notApprovable && (
              <div className="note warn" role="alert">
                <strong>This campaign cannot be approved now</strong>
                <p>{approveErr?.message}</p>
                <p style={{ margin: 0 }}>{approveErr?.remedy}</p>
              </div>
            )}

            {approveError != null && !selfApproval && !notApprovable && (
              <ErrorBox error={approveError} what="The approval" />
            )}

            {holding && (
              <>
                {isMaker && !selfApproval && (
                  <div className="note warn">
                    <strong>You raised this campaign, so a second person approves it</strong> Ask an approver to open
                    this page
                  </div>
                )}

                <h3>What approving will do</h3>
                {openByVendor === null ? null : (
                  <>
                    <dl className="metrics">
                      <div className="metric">
                        <dt>Will be issued</dt>
                        <dd>{forecast.willIssue.length}</dd>
                      </div>
                      <div className="metric">
                        <dt>Will be suppressed</dt>
                        <dd>{forecast.willSuppress.length}</dd>
                      </div>
                      <div className="metric">
                        <dt>{willEmail ? 'Invitations queued' : 'Invitations recorded, not emailed'}</dt>
                        <dd>{forecast.willIssue.length}</dd>
                      </div>
                    </dl>
                    {forecast.willSuppress.length > 0 && (
                      <ul className="outcome-list">
                        {forecast.willSuppress.map((s) => (
                          <li key={s.row.id}>
                            <strong>{s.row.vendorName || s.row.vendorCode || s.row.vendorId}</strong>
                            <span className="cell-sub">already open on {s.reference}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}

                {/* The refusal names roles and can run long, so it opens from
                    the (i) beside a short label. */}
                {!canIssue && (
                  <p className="field-note sw-row" style={{ marginTop: 0 }}>
                    <Status tone="neutral">Read only</Status>
                    <ReasonTip label="Why you cannot approve" text={refusalReason('questionnaire.issue')} />
                  </p>
                )}

                {!armed ? (
                  <button
                    type="button"
                    className="primary"
                    onClick={() => setArmed(true)}
                    disabled={approving || !canIssue}
                    title={canIssue ? undefined : refusalReason('questionnaire.issue')}
                  >
                    Approve and issue
                  </button>
                ) : (
                  <div className="confirm">
                    {willEmail ? (
                      <strong>
                        This issues {forecast.willIssue.length} assessment
                        {forecast.willIssue.length === 1 ? '' : 's'} and queues {forecast.willIssue.length} invitation
                        {forecast.willIssue.length === 1 ? '' : 's'} to real vendor addresses
                      </strong>
                    ) : (
                      <strong>
                        This issues {forecast.willIssue.length} assessment
                        {forecast.willIssue.length === 1 ? '' : 's'} and emails nobody: {whyNotEmailed(mailNow).toLowerCase()} The
                        invitations are recorded, to resend once mail is on
                      </strong>
                    )}
                    <p>There is no unsend, assessments issued here are cancelled one at a time by hand</p>
                    <div className="chiprow">
                      <button
                        type="button"
                        className="primary"
                        data-action="tprm.campaign.a01"
                        onClick={approve}
                        disabled={approving}
                      >
                        {approving ? 'Approving' : 'Yes, approve and issue'}
                      </button>
                      <button type="button" onClick={() => setArmed(false)} disabled={approving}>
                        Not now
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {!holding && !outcome && (
              <p className="muted" style={{ margin: 0 }}>
                {campaign.status === 'CANCELLED'
                  ? 'Cancelled before anything was issued'
                  : 'Approved already, the recipients below carry what it produced'}
                {campaign.status !== 'CANCELLED' && (
                  <>
                    {' '}
                    <Toggletip label="Why it cannot be approved again">Approval happens once.</Toggletip>
                  </>
                )}
              </p>
            )}
          </section>

          <section className="panel panel-flush" aria-labelledby="recipients-heading">
            <div className="panel-head">
              <h2 id="recipients-heading" style={{ margin: 0 }}>
                Recipients
              </h2>
            </div>
            <DataTable
              caption={
                'Recipients of ' +
                campaign.name +
                ', with the address invited, the delivery state, the assessment issued where there is one and the reason where the invitation was held back.'
              }
              columns={columns}
              rows={recipients}
              rowKey={(r) => r.id}
              defaultSort={{ key: 'vendor', dir: 'asc' }}
              empty={{
                title: 'This campaign has no recipients',
                detail: (
                  <>
                    A campaign with nobody on it cannot be approved{' '}
                    <Toggletip label="How recipients are attached">
                      Vendors are attached when the campaign is created, and a vendor with no contactable address is left
                      off.
                    </Toggletip>
                  </>
                ),
              }}
              footer={
                <ListCount shown={counts.total} noun="on the list" one="on the list">
                  , {counts.issued} issued, {counts.suppressed} suppressed, {counts.pending} not yet issued
                  {counts.notMailed > 0 ? ', ' + counts.notMailed + ' recorded, not emailed' : ''}
                </ListCount>
              }
            />
          </section>
        </div>

        <div>
          <section className="panel">
            <h2>Campaign</h2>
            <dl className="kv">
              <dt>Status</dt>
              <dd>
                <CampaignStatus status={campaign.status} mail={mail} />
              </dd>
              <dt>Mode</dt>
              <dd>{modeLabel(campaign.mode)}</dd>
              <dt>Due</dt>
              <dd className="mono">{campaign.dueOn ? shortDate(campaign.dueOn) : <span className="muted">no date</span>}</dd>
              <dt>Raised by</dt>
              <dd className="mono">{campaign.createdBy}</dd>
              <dt>Approved by</dt>
              <dd className="mono">
                {campaign.approvedBy || <span className="muted">nobody yet</span>}
                {campaign.approvedAt && <span className="cell-sub">{shortDate(campaign.approvedAt)}</span>}
              </dd>
              <dt>Recipients</dt>
              <dd className="mono">{campaign.recipientCount ?? counts.total}</dd>
              <dt>Questionnaire</dt>
              <dd className="mono" style={{ fontSize: 12 }}>
                {campaign.questionnaireVersionId || <span className="muted">none bound</span>}
              </dd>
              <dt>Risk model</dt>
              <dd className="mono" style={{ fontSize: 12 }}>
                {campaign.riskModelVersionId || (
                  <span className="muted">none, so answers are collected and not scored</span>
                )}
              </dd>
            </dl>
          </section>

          <section className="panel">
            <h2>Stop this campaign</h2>
            {holding ? (
              <>
                {cancelError != null && <ErrorBox error={cancelError} what="The cancellation" />}
                {!cancelOpen ? (
                  <button
                    type="button"
                    className="danger"
                    onClick={() => setCancelOpen(true)}
                    disabled={!canPlan}
                    title={canPlan ? undefined : refusalReason('assessment.plan')}
                  >
                    Cancel campaign
                  </button>
                ) : (
                  <form onSubmit={cancel} noValidate>
                    <div className="field">
                      <label htmlFor="cancel-reason">Reason for cancelling</label>
                      <textarea
                        id="cancel-reason"
                        rows={3}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        aria-describedby={cancelIssue ? 'cancel-reason-issue' : 'cancel-reason-note'}
                        aria-invalid={cancelIssue ? true : undefined}
                      />
                      {cancelIssue ? (
                        <span className="field-error" id="cancel-reason-issue" role="alert">
                          {cancelIssue}
                        </span>
                      ) : null}
                    </div>
                    <div className="chiprow" style={{ marginTop: 8 }}>
                      <button type="submit" className="danger" data-action="tprm.campaign.a02" disabled={cancelling}>
                        {cancelling ? 'Cancelling' : 'Cancel this campaign'}
                      </button>
                      <button type="button" onClick={() => setCancelOpen(false)} disabled={cancelling}>
                        Keep it
                      </button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <p className="muted" style={{ margin: 0 }}>
                {campaign.status === 'CANCELLED'
                  ? 'Already cancelled'
                  : 'Too late to cancel, assessments it issued are cancelled one by one'}
              </p>
            )}
          </section>
        </div>
      </div>
    </>
  )
}
